import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
} from "@apollo/client"
import { getMainDefinition } from "@apollo/client/utilities"
import { RetryLink } from "@apollo/client/link/retry"
import { setContext } from "@apollo/client/link/context"
import useUtm from "@src/hooks/use-utm"

// Create retry link for IndexedDB and connection errors
const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true
  },
  attempts: {
    max: 3,
    retryIf: (error, operation) => {
      // Never retry mutations — they may have non-idempotent side effects
      // (e.g., Authorize.net OTS tokens are consumed on first use)
      const definition = getMainDefinition(operation.query)
      if (
        definition.kind === "OperationDefinition" &&
        definition.operation === "mutation"
      ) {
        return false
      }

      const errorMessage = error.message || ""
      return (
        errorMessage.includes("IndexedDB") ||
        errorMessage.includes("Connection to Indexed Database") ||
        errorMessage.includes("Network request failed") ||
        errorMessage.includes("fetch")
      )
    }
  }
})

export const apiLink = new HttpLink({
  credentials: "include",
  // useGETForQueries: true,
  uri: `${process.env.NEXT_PUBLIC_API_BASE_URL}/graphql`,
})

export const storyblokLink = new HttpLink({
  uri: `https://gapi.storyblok.com/v1/api`,
})

const client = new ApolloClient({
  cache: new InMemoryCache({
    resultCaching: false,
  }),
  defaultOptions: {
    watchQuery: {
      errorPolicy: "all",
      fetchPolicy: "no-cache",
    },
    query: {
      errorPolicy: "all",
      fetchPolicy: "no-cache",
    },
    mutate: {
      errorPolicy: "all",
      fetchPolicy: "no-cache",
    },
  },
  link: ApolloLink.from([
    retryLink,
    ApolloLink.split(
      (operation) => operation.getContext().clientName === "storyblok",
      storyblokLink,
      apiLink
    )
  ]),
  ssrMode: true,
})

export function addIdTokenToApolloClient(idToken: string | undefined) {
  if (!idToken) {
    return
  }
  const utms = useUtm()
  const idTokenLink = setContext((_, { headers }) => ({
    headers: {
      ...headers,
      "firebase-id-token": idToken || null,
      ...(utms && { utms: JSON.stringify(utms) }),
    },
  }))
  client.setLink(
    ApolloLink.from([
      retryLink,
      ApolloLink.split(
        (operation) => operation.getContext().clientName === "storyblok",
        storyblokLink,
        idTokenLink.concat(apiLink)
      )
    ])
  )
}

export default client
